// useBuildings — PowerSync-backed hook for the SiteDetailPage's
// building list. Replaces the REST-backed `useCaptureBuildings` hook
// for Phase D-2 of the PowerSync rollout.
//
// Three queries fanned out into local SQLite:
//   1. buildings  — the site's buildings, ordered by §5.4a
//   2. sites      — the site row itself, for the breadcrumb middle
//                   segment
//   3. bases      — the parent base row, for the breadcrumb root
//
// All three are scoped to the user's `assigned_sites` claim through
// the `reference_site_scoped` stream, so the local mirror only
// contains rows the user has access to. The hook applies no further
// authorization on top.
//
// Stale-data guard: navigating from /capture/sites/A → /capture/sites/B
// keeps `useQuery` returning A's rows until B's query resolves. Without
// the explicit site-id check, the user would see A's buildings under
// B's URL during the transition — a contractually significant
// accuracy failure (asset counts attributed to the wrong site).
//
// Initial-sync gate: when the `reference_site_scoped` stream hasn't
// delivered its first sync, an empty result is NOT authoritative —
// the screen would otherwise flash "No buildings yet in this site"
// before the stream populates local SQLite.
//
// Imports kept on single lines so lint:architecture's awk scan
// recognises each as an import and applies the hooks/ exemption.
// Mirrors the pattern in use-sites.ts.
import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import type { BuildingStatus } from '@citycare/shared'

import { db } from './client'
import type { BuildingRecord } from './schema'

/**
 * Building row shape returned by the hook. Snake_case mirrors the
 * SQLite columns; the parent SiteDetailPage maps to camelCase as
 * needed for its `BuildingMinimalRow` props.
 */
export interface UseBuildingsRow extends BuildingRecord {
  /** id is auto-created by PowerSync — always present at runtime */
  id: string
  /** Narrowed to BuildingStatus enum so consumers don't have to
   * re-cast the loose `string` from BuildingRecord. */
  status: BuildingStatus
  /** Convenience boolean projection of the integer-mirrored boolean */
  is_field_added: number
  requires_review: number
}

/**
 * Parent envelope (site + base). Mirrors the
 * `CaptureBuildingsParent` shape from the REST hook so the
 * SiteDetailPage breadcrumb keeps working without changes.
 */
export interface UseBuildingsParent {
  site: { id: string; name: string }
  base: { id: string; name: string }
}

export interface UseBuildingsResult {
  buildings: UseBuildingsRow[]
  parent: UseBuildingsParent | null
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

interface SiteSummaryRow {
  id: string
  name: string
  base_id: string
}

interface BaseSummaryRow {
  id: string
  name: string
}

/**
 * Returns the building list for a given site, plus the parent
 * site/base summaries needed to render the breadcrumb. Reactive:
 * any sync-delivered change to `buildings`, `sites`, or `bases`
 * triggers an automatic re-render.
 */
export function useBuildings(siteId: string): UseBuildingsResult {
  const buildingsResult = useQuery<UseBuildingsRow>(
    `SELECT
       id,
       site_id,
       name,
       floc,
       floor_area_m2,
       status,
       building_type_id,
       is_field_added,
       requires_review,
       display_order,
       created_at,
       updated_at
     FROM buildings
     WHERE site_id = ?
       -- Admin-only statuses are hidden from field staff: a building
       -- flagged to_be_demolished / out_of_scope is not capture work.
       -- The admin Locations pages list them via the REST endpoints.
       AND status NOT IN ('to_be_demolished', 'out_of_scope')
     ORDER BY display_order ASC, name ASC`,
    [siteId],
  )

  const siteResult = useQuery<SiteSummaryRow>(
    `SELECT id, name, base_id FROM sites WHERE id = ? LIMIT 1`,
    [siteId],
  )

  // Bases are filtered indirectly: the base_id used here is whatever
  // the site row carries. We rebuild the parent envelope only when
  // both queries resolve to data matching the requested siteId.
  const parentBaseId = siteResult.data[0]?.base_id ?? ''
  const baseResult = useQuery<BaseSummaryRow>(
    `SELECT id, name FROM bases WHERE id = ? LIMIT 1`,
    [parentBaseId],
  )

  // Scoped sync-readiness — the same stream the hook below depends
  // on. Empty arrays are not authoritative until the stream has
  // delivered at least one full sync.
  const referenceSiteScopedSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'reference_site_scoped', waitForStream: true },
  ])

  // Stale-data guard: every returned building's site_id must match
  // the requested siteId; the site summary's id must match too.
  // Empty arrays are not stale — they're either a true empty state
  // OR the initial "no sync yet" state (handled by the next gate).
  const buildingsAreForSiteId =
    buildingsResult.data.length === 0 ||
    buildingsResult.data.every((b) => b.site_id === siteId)
  const siteIsForSiteId =
    siteResult.data.length === 0 ||
    (siteResult.data[0]?.id ?? '') === siteId
  const isStale = !buildingsAreForSiteId || !siteIsForSiteId

  // Initial-sync gating: a freshly-installed device may have an
  // empty mirror until the reference_site_scoped stream's first
  // sync completes. Suppress the empty state during that window
  // to avoid a "No buildings yet" flash on the primary workflow.
  const buildingsAreEmptyButUnsynced =
    buildingsResult.data.length === 0 && !referenceSiteScopedSynced

  const isLoading =
    buildingsResult.isLoading ||
    siteResult.isLoading ||
    baseResult.isLoading ||
    isStale ||
    buildingsAreEmptyButUnsynced
  const isFetching =
    buildingsResult.isFetching ||
    siteResult.isFetching ||
    baseResult.isFetching
  const error =
    buildingsResult.error ?? siteResult.error ?? baseResult.error

  // Parent envelope: only build it when both site and base rows are
  // present and match the requested siteId. Otherwise null — the
  // breadcrumb falls back to a generic "Site" label until data lands.
  let parent: UseBuildingsParent | null = null
  const site = siteResult.data[0]
  const base = baseResult.data[0]
  if (site !== undefined && site.id === siteId && base !== undefined && base.id === parentBaseId) {
    parent = {
      site: { id: site.id, name: site.name },
      base: { id: base.id, name: base.name },
    }
  }

  return {
    buildings: isStale ? [] : buildingsResult.data,
    parent,
    isLoading,
    isFetching,
    error,
  }
}
